import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import * as readApi from '../api/readApi';
import { patchScheduleMatch, patchDashboardMatch } from '../api/readApi';
import { formatDate, sortMatchesChronologically } from '../utils/dateFormat';
import { formatDateInTimezone, formatTimeInTimezone, getDateKeyInTimezone, parseLocalToUTC, toUtcMs, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import { getNextAvailableSlot, migrateScheduleConfig, isSlotTaken } from '../utils/scheduleStrategy';
import { getUserDisplayName } from '../utils/nameFormat';
import * as matchApi from '../api/matchApi';
import { logMatchUpdate, LOG_TYPES } from '../services/matchUpdateLogService';
import { ensureVenueBlockForMatch } from '../services/venueBlockService';
import { createMatchEvent, updateMatchEvent } from '../services/googleCalendarService';
import {
  saveMatchCalendarEvent,
  getMatchCalendarEvent,
  updateMatchCalendarEventScheduledAt,
  getParticipantEmailsForMatch,
} from '../services/matchCalendarService';
import { checkCalendarDeclinesAndReschedule } from '../services/calendarDeclineService';
import { getCalendarConnected, setCalendarConnected } from '../services/userCalendarSettingsService';
import { useAlert } from '../contexts/AlertContext';
import { useOfflineQueue } from '../contexts/OfflineQueueContext';
import { useConflict } from '../contexts/ConflictContext';
import PlayerNameWithApartment from '../components/PlayerNameWithApartment';
import CachedAvatar from '../components/CachedAvatar';
import MatchUpdateHistoryModal from '../components/MatchUpdateHistoryModal';
import RescheduleRequestModal from '../components/RescheduleRequestModal';
import MatchReactionStrip from '../components/MatchReactionStrip';
import MatchReactionsModal from '../components/MatchReactionsModal';
import GoogleAd from '../components/GoogleAd';
import DataFetchOverlay from '../components/DataFetchOverlay';
import PlayerAvailabilityAdminOverlay from '../components/PlayerAvailabilityAdminOverlay';
import './MatchSchedule.css';

export default function MatchSchedule() {
  const { currentUser, userProfile, requestCalendarAccess } = useAuth();
  const { gameId } = useGame();
  const gameIdStr = gameId || 'table-tennis';
  const userTimezone = userProfile?.timezone || DEFAULT_TIMEZONE;
  const { getPlayers, getPlayerName, getPlayerPhotoUrl } = usePlayerCache();
  const { showAlert, showToast } = useAlert();
  const { isOnline, queueReschedule } = useOfflineQueue();
  const { addConflict } = useConflict();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // upcoming (default), all, my-matches, scheduled, completed
  const [historyModalMatchId, setHistoryModalMatchId] = useState(null);
  const [rescheduleModalMatch, setRescheduleModalMatch] = useState(null);
  const [reactionsModalMatchId, setReactionsModalMatchId] = useState(null);
  const [addingToCalendarMatchId, setAddingToCalendarMatchId] = useState(null);
  const [calendarEventsByMatchId, setCalendarEventsByMatchId] = useState({});
  const [calendarConnected, setCalendarConnectedState] = useState(null);
  const [calendarSyncing, setCalendarSyncing] = useState(false);
  const [showAvailabilityAdminOverlay, setShowAvailabilityAdminOverlay] = useState(false);
  const [calendarAvailabilityExpanded, setCalendarAvailabilityExpanded] = useState(false);
  const calendarTokenPromiseRef = useRef(null);

  const adminTournaments = (tournaments || []).filter(
    (t) =>
      (t.createdBy || '') === currentUser?.uid ||
      (Array.isArray(t.adminIds) && t.adminIds.includes(currentUser?.uid))
  );

  /** Single in-flight calendar token request so multiple effects don't open duplicate popups.
   * @param {boolean} [forceConsent=false] – true only for first-time "Connect Google Calendar"; false for Resync / updates so user isn't re-prompted. */
  async function getCalendarToken(forceConsent = false) {
    if (calendarTokenPromiseRef.current) {
      try {
        return await calendarTokenPromiseRef.current;
      } finally {
        calendarTokenPromiseRef.current = null;
      }
    }
    const p = requestCalendarAccess(forceConsent);
    calendarTokenPromiseRef.current = p;
    try {
      return await p;
    } finally {
      calendarTokenPromiseRef.current = null;
    }
  }

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    fetchMatches();
    // Only refetch when user or game changes; filter is client-side. Avoid refetch on every userProfile change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, gameId]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.gameId === (gameId || 'table-tennis')) fetchMatches();
    };
    window.addEventListener('scheduleCachePatched', handler);
    window.addEventListener('scheduleCacheUpdated', handler);
    return () => {
      window.removeEventListener('scheduleCachePatched', handler);
      window.removeEventListener('scheduleCacheUpdated', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  async function fetchMatches() {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await readApi.getScheduleData(gameId);
      const matchesList = (data.matches || []).map((m) => ({ ...m }));
      matchesList.sort(sortMatchesChronologically);
      setMatches(matchesList);
      setTournaments(data.tournaments || []);
      if (data.playerNames && Object.keys(data.playerNames).length) {
        try {
          await getPlayers(Object.keys(data.playerNames));
        } catch (playerErr) {
          console.warn('Preloading player names failed; schedule still valid:', playerErr);
        }
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }

  // Load calendar event status for user's scheduled matches (to show Update vs Add)
  useEffect(() => {
    if (!currentUser?.uid || matches.length === 0) return;
    let cancelled = false;
    const myScheduled = matches.filter((m) => {
      if (m.status !== 'scheduled') return false;
      if (m.matchType === 'doubles') {
        return (m.team1Players || []).includes(currentUser.uid) || (m.team2Players || []).includes(currentUser.uid);
      }
      return m.player1Id === currentUser.uid || m.player2Id === currentUser.uid;
    });
    Promise.all(myScheduled.map((m) => getMatchCalendarEvent(m.id, currentUser.uid)))
      .then((results) => {
        if (cancelled) return;
        const map = {};
        myScheduled.forEach((m, i) => {
          const r = results[i];
          if (r?.eventId) map[m.id] = r;
        });
        setCalendarEventsByMatchId(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [matches, currentUser?.uid]);

  // Load calendar connected status (catch permission errors – rules may not be deployed yet)
  useEffect(() => {
    if (!currentUser?.uid) return;
    let cancelled = false;
    getCalendarConnected(currentUser.uid)
      .then((connected) => {
        if (!cancelled) setCalendarConnectedState(connected);
      })
      .catch(() => {
        if (!cancelled) setCalendarConnectedState(false);
      });
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  /** Sync all user's scheduled matches to Google Calendar (add new, update rescheduled). */
  async function syncAllMatchesToCalendar(token) {
    if (!token || !currentUser?.uid) return;
    const myScheduled = matches.filter((m) => {
      if (m.status !== 'scheduled' || !(m.scheduledAt ?? m.scheduledDate)) return false;
      if (m.matchType === 'doubles') {
        return (m.team1Players || []).includes(currentUser.uid) || (m.team2Players || []).includes(currentUser.uid);
      }
      return m.player1Id === currentUser.uid || m.player2Id === currentUser.uid || m.refereeId === currentUser.uid;
    });
    const map = { ...calendarEventsByMatchId };
    let didCreateSharedEvent = false;
    for (const match of myScheduled) {
      try {
        const currentMs = toUtcMs(match.scheduledAt ?? match.scheduledDate) ?? NaN;
        if (isNaN(currentMs) || currentMs <= 0) continue;
        const tournament = tournaments.find((t) => t.id === match.tournamentId);
        const tournamentName = tournament?.name || 'Tournament';
        const isReferee = match.refereeId === currentUser.uid;
        const m = {
          ...match,
          scheduledDate: match.scheduledAt ?? match.scheduledDate,
          player1Name: match.player1Name || 'Player 1',
          player2Name: match.player2Name || 'Player 2',
        };

        if (match.calendarEventId && match.calendarEventOwnerId === currentUser.uid) {
          const needsUpdate =
            !match.calendarEventScheduledAtMs ||
            Math.abs(currentMs - (match.calendarEventScheduledAtMs || 0)) > 60000;
          if (needsUpdate) {
            await updateMatchEvent(token, match.calendarEventId, m, tournamentName, { isReferee });
            await updateDoc(doc(db, 'matches', match.id), {
              calendarEventScheduledAtMs: currentMs,
              updatedAt: new Date(),
            });
          }
          const declineResult = await checkCalendarDeclinesAndReschedule(match, token);
          if (declineResult.rescheduled && declineResult.declinedUserIds?.length > 0) {
            if (declineResult.newDate != null && declineResult.newTime != null) {
              const updatedM = {
                ...m,
                scheduledDate: declineResult.newDate,
                scheduledTime: declineResult.newTime,
                scheduledAt: declineResult.newDate instanceof Date ? declineResult.newDate.getTime() : toUtcMs(declineResult.newDate),
              };
              await updateMatchEvent(token, match.calendarEventId, updatedM, tournamentName, { isReferee });
              await updateDoc(doc(db, 'matches', match.id), {
                calendarEventScheduledAtMs: updatedM.scheduledAt ?? toUtcMs(declineResult.newDate),
                updatedAt: new Date(),
              });
            }
            showToast?.('A player declined the invite. Match rescheduled to next available slot.', { variant: 'info' });
            if (typeof fetchMatches === 'function') fetchMatches();
          }
          continue;
        }
        if (match.calendarEventId && match.calendarEventOwnerId !== currentUser.uid) {
          continue;
        }

        const record = map[match.id] ?? (await getMatchCalendarEvent(match.id, currentUser.uid));
        if (record?.eventId) {
          const needsUpdate = record.scheduledAtMs == null || Math.abs(currentMs - record.scheduledAtMs) > 60000;
          if (needsUpdate) {
            await updateMatchEvent(token, record.eventId, m, tournamentName, { isReferee });
            await updateMatchCalendarEventScheduledAt(match.id, currentUser.uid, currentMs);
            map[match.id] = { ...record, scheduledAtMs: currentMs };
          }
        } else {
          const attendeeEmails = await getParticipantEmailsForMatch(match);
          const result = await createMatchEvent(token, m, tournamentName, {
            isReferee,
            attendeeEmails: attendeeEmails.length > 0 ? attendeeEmails : undefined,
          });
          if (result?.id) {
            await saveMatchCalendarEvent(match.id, currentUser.uid, result.id, currentMs);
            map[match.id] = { eventId: result.id, scheduledAtMs: currentMs };
            if (attendeeEmails.length > 0) {
              await updateDoc(doc(db, 'matches', match.id), {
                calendarEventId: result.id,
                calendarEventOwnerId: currentUser.uid,
                calendarEventScheduledAtMs: currentMs,
                updatedAt: new Date(),
              });
              didCreateSharedEvent = true;
            }
          }
        }
      } catch (err) {
        console.warn('[MatchSchedule] Sync failed for match', match.id, err?.message);
      }
    }
    setCalendarEventsByMatchId(map);
    if (didCreateSharedEvent && typeof fetchMatches === 'function') fetchMatches();
  }

  // Calendar sync runs only when user clicks "Connect Google Calendar" or "Resync calendar now"
  // (no auto token request on schedule view – consent only when user opts into calendar sync)

  async function handleConnectAndSync() {
    if (!currentUser?.uid) return;
    setAddingToCalendarMatchId('connect');
    try {
      const token = await getCalendarToken(true); // first-time connect: show consent once
      if (!token) throw new Error('Could not get calendar access');
      await setCalendarConnected(currentUser.uid, true);
      setCalendarConnectedState(true);
      await syncAllMatchesToCalendar(token);
      showToast('Calendar connected. All your matches will sync automatically.', { variant: 'success' });
    } catch (err) {
      console.error('Connect calendar failed:', err);
      showAlert(err?.message || 'Could not connect calendar. Please try again.', { variant: 'error' });
    } finally {
      setAddingToCalendarMatchId(null);
    }
  }

  async function handleResyncCalendar() {
    if (!currentUser?.uid) return;
    setAddingToCalendarMatchId('resync');
    setCalendarSyncing(true);
    try {
      const token = await getCalendarToken(false); // already granted: use cached consent
      if (!token) throw new Error('Could not get calendar access');
      await syncAllMatchesToCalendar(token);
      showToast('Calendar resynced. App-created events updated.', { variant: 'success' });
    } catch (err) {
      console.error('Resync calendar failed:', err);
      showAlert(err?.message || 'Could not resync calendar. Please try again.', { variant: 'error' });
    } finally {
      setAddingToCalendarMatchId(null);
      setCalendarSyncing(false);
    }
  }

  function getMyMatches() {
    if (!currentUser) return [];
    return matches.filter(match => {
      if (match.matchType === 'doubles') {
        const inTeam1 = match.team1Players && match.team1Players.includes(currentUser.uid);
        const inTeam2 = match.team2Players && match.team2Players.includes(currentUser.uid);
        return inTeam1 || inTeam2 || match.refereeId === currentUser.uid;
      } else {
        return match.player1Id === currentUser.uid ||
               match.player2Id === currentUser.uid ||
               match.refereeId === currentUser.uid;
      }
    });
  }

  /** Partition my matches into three mutually exclusive sections: referee, singles, doubles. */
  function getMyMatchesByRole() {
    const myMatches = getMyMatches();
    const referee = [];
    const singles = [];
    const doubles = [];
    for (const m of myMatches) {
      const isReferee = m.refereeId === currentUser?.uid;
      const isPlayerSingles = m.matchType === 'singles' && (m.player1Id === currentUser?.uid || m.player2Id === currentUser?.uid);
      const isPlayerDoubles = m.matchType === 'doubles' &&
        ((m.team1Players && m.team1Players.includes(currentUser?.uid)) ||
         (m.team2Players && m.team2Players.includes(currentUser?.uid)));
      if (isPlayerSingles) singles.push(m);
      else if (isPlayerDoubles) doubles.push(m);
      else if (isReferee) referee.push(m);
    }
    return { referee, singles, doubles };
  }

  /** Upcoming = not completed, in chronological order (scheduled/in-progress by time, then unschedulable). */
  function getUpcomingMatches() {
    const nonCompleted = matches.filter(
      (m) => m.status === 'scheduled' || m.status === 'in-progress' || m.status === 'unschedulable'
    );
    const withTime = nonCompleted.filter((m) => (m.scheduledAt ?? m.scheduledDate) != null);
    const unschedulable = nonCompleted.filter((m) => (m.scheduledAt ?? m.scheduledDate) == null);
    withTime.sort(sortMatchesChronologically);
    return [...withTime, ...unschedulable];
  }

  function getFilteredMatches() {
    switch (filter) {
      case 'upcoming':
        return getUpcomingMatches();
      case 'my-matches':
        return getMyMatches();
      case 'scheduled':
        return matches.filter(m => m.status === 'scheduled');
      case 'completed':
        return matches.filter(m => m.status === 'completed');
      default:
        return matches;
    }
  }

  /** Teams for approval: doubles = [team1, team2], singles = [player1], [player2]. */
  function getTeamsForApproval(match) {
    if (!match) return [];
    if (match.matchType === 'doubles') {
      const t1 = (match.team1Players || []).filter(Boolean);
      const t2 = (match.team2Players || []).filter(Boolean);
      return t1.length && t2.length ? [t1, t2] : [];
    }
    return [[match.player1Id], [match.player2Id]].filter((t) => t[0]);
  }

  /** True when at least one player from each team has approved. */
  function hasEnoughApprovals(approvedBy, teams) {
    if (!teams.length) return false;
    const list = Array.isArray(approvedBy) ? approvedBy : approvedBy ? [approvedBy] : [];
    return teams.every((team) => team.some((id) => list.includes(id)));
  }

  async function handleRequestReschedule(matchId, requestedDate, requestedTime) {
    try {
      const userName = getUserDisplayName({ displayName: currentUser.displayName, email: currentUser.email });
      const reqDate = requestedDate instanceof Date ? requestedDate.toISOString().slice(0, 10) : requestedDate;
      await matchApi.matchRescheduleRequest(matchId, { requestedDate: reqDate, requestedTime, reason: '' }, userName);
      fetchMatches();
      setRescheduleModalMatch(null);
      showToast('Reschedule request sent. One player from each team (or admin/referee) must approve.', { variant: 'success' });
    } catch (err) {
      console.error('Error requesting reschedule:', err);
      showAlert('Error requesting reschedule: ' + (err?.message || err), { variant: 'error' });
    }
  }

  async function handleApproveReschedule(matchId) {
    try {
      const match = matches.find(m => m.id === matchId);
      if (!match) return;

      const isReferee = currentUser && match.refereeId === currentUser.uid;
      const isAdmin = userProfile?.isAdmin || userProfile?.isSuperAdmin;
      const canApproveWithoutPlayers = isReferee || isAdmin;

      const teams = getTeamsForApproval(match);
      const approvedBy = Array.isArray(match.rescheduleApprovedBy)
        ? [...match.rescheduleApprovedBy]
        : (match.rescheduleApprovedBy ? [match.rescheduleApprovedBy] : []);

      if (!canApproveWithoutPlayers) {
        if (!approvedBy.includes(currentUser.uid)) {
          approvedBy.push(currentUser.uid);
        }
        await updateDoc(doc(db, 'matches', matchId), {
          rescheduleApprovedBy: approvedBy
        });
        const userName = getUserDisplayName({ displayName: currentUser.displayName, email: currentUser.email });
        await logMatchUpdate(matchId, LOG_TYPES.RESCHEDULE_APPROVED, { performedByName: userName }, currentUser.uid, userName);

        const enoughApprovals = hasEnoughApprovals(approvedBy, teams);
        if (!enoughApprovals) {
          fetchMatches();
          showToast('Your approval has been recorded. Waiting for one player from each team to approve.', { variant: 'info' });
          return;
        }
      }

      let scheduleConfig = { scheduleStart: '18:00', scheduleEnd: '21:00', matchDuration: 30, weekdays: [1, 3, 5] };
      let otherScheduled = [];
      const slotInfo = await readApi.getScheduleSlotInfo(matchId);
      if (slotInfo.tournament?.scheduleConfig) {
        scheduleConfig = migrateScheduleConfig(slotInfo.tournament.scheduleConfig || {});
      }
      if (Array.isArray(slotInfo.otherScheduled)) {
        otherScheduled = slotInfo.otherScheduled;
      }

      let slot = null;

      if (match.rescheduleRequestedDate && match.rescheduleRequestedTime) {
        const reqDate = (() => {
          const ms = toUtcMs(match.rescheduleRequestedDate);
          return ms != null ? new Date(ms) : null;
        })();
        const reqTime = match.rescheduleRequestedTime;
        if (!isSlotTaken(reqDate, reqTime, otherScheduled, matchId)) {
          slot = { date: reqDate, time: reqTime };
        }
      }

      if (!slot) {
        slot = getNextAvailableSlot(scheduleConfig, otherScheduled);
      }
      if (!slot) {
        fetchMatches();
        showAlert('No available slot found. The requested slot may be taken. Try requesting a different date/time or contact an admin.', { variant: 'warning' });
        return;
      }

      const durationMins = scheduleConfig.matchDuration || 30;
      const updatedMatch = {
        id: matchId,
        venueId: match.venueId,
        courtId: match.courtId,
        scheduledDate: slot.date,
        scheduledTime: slot.time,
      };
      const venueBlockId = match.venueId
        ? await ensureVenueBlockForMatch(updatedMatch, durationMins)
        : null;
      const userName = getUserDisplayName({ displayName: currentUser.displayName, email: currentUser.email });
      const slotDateMs = slot.date instanceof Date ? slot.date.getTime() : slot.date;
      const dateStr = getDateKeyInTimezone(slotDateMs, userTimezone);
      const scheduledAt = parseLocalToUTC(dateStr, slot.time, userTimezone);
      const previousMatches = [...matches];
      setMatches((prev) =>
        prev.map((m) =>
          m.id === matchId
            ? {
                ...m,
                scheduledAt,
                scheduledDate: scheduledAt,
                scheduledTime: slot.time,
                status: 'scheduled',
                rescheduleRequested: false,
                rescheduleRequestedBy: null,
                rescheduleRequestedDate: null,
                rescheduleRequestedTime: null,
                rescheduleApprovedBy: [],
              }
            : m
        )
      );
      try {
        if (!isOnline()) {
          queueReschedule(
            {
              matchId,
              scheduledAt,
              venueBlockId,
              actorName: userName,
            },
            { matchId, updatedAt: match?.updatedAt ?? null }
          );
          showToast('Saved locally. Will sync when back online.', { variant: 'info' });
          return;
        }
        const result = await matchApi.matchRescheduleComplete(
          matchId,
          { scheduledAt, venueBlockId, clientUpdatedAt: match?.updatedAt ?? undefined },
          userName
        );
        if (result?.conflict && result?.serverMatch) {
          addConflict({
            matchId,
            serverMatch: result.serverMatch,
            pendingPayload: { matchId, scheduledAt, venueBlockId, actorName: userName },
            queueItemId: null,
          });
          showToast('Someone else updated this match. Please choose which version to keep.', { variant: 'warning' });
          fetchMatches();
          return;
        }
        showToast('Match rescheduled successfully.', { variant: 'success' });
        const matchUpdates = {
          scheduledAt,
          scheduledDate: slot.date,
          scheduledTime: slot.time,
          rescheduleRequested: false,
          rescheduleApproved: false,
          rescheduleRequestedBy: null,
          rescheduleApprovedBy: null,
          requestedFor: null,
          requestedTime: null,
          rescheduleReason: null,
          updatedAt: Date.now(),
        };
        patchScheduleMatch(gameIdStr, matchId, matchUpdates);
        patchDashboardMatch(gameIdStr, matchId, matchUpdates);
        setMatches((prev) =>
          prev.map((m) =>
            m.id === matchId ? { ...m, ...matchUpdates } : m
          ).sort(sortMatchesChronologically)
        );
        if (match.calendarEventId && match.calendarEventOwnerId === currentUser.uid) {
          try {
            const token = await getCalendarToken(false); // already connected: no re-prompt
            if (token) {
              const tournament = tournaments.find((t) => t.id === match.tournamentId);
              const tournamentName = tournament?.name || 'Tournament';
              const isReferee = match.refereeId === currentUser.uid;
              const m = {
                ...match,
                scheduledDate: slot.date,
                scheduledTime: slot.time,
                scheduledAt: slot.date instanceof Date ? slot.date.getTime() : toUtcMs(slot.date),
                player1Name: match.player1Name || 'Player 1',
                player2Name: match.player2Name || 'Player 2',
              };
              await updateMatchEvent(token, match.calendarEventId, m, tournamentName, { isReferee });
              await updateDoc(doc(db, 'matches', matchId), {
                calendarEventScheduledAtMs: m.scheduledAt ?? toUtcMs(slot.date),
                updatedAt: new Date(),
              });
            }
          } catch (calErr) {
            console.warn('[MatchSchedule] Calendar update after approve reschedule failed', calErr?.message);
          }
        }
      } catch (apiErr) {
        setMatches(previousMatches);
        console.error('Error approving reschedule:', apiErr);
        const msg = apiErr?.message || String(apiErr);
        const isPermission = /permission|insufficient|denied/i.test(msg);
        showAlert(
          isPermission
            ? "You don't have permission to approve this reschedule. Make sure you're a player in this match, the referee, or an admin."
            : 'Reschedule failed: ' + msg,
          { variant: 'error' }
        );
      }
    } catch (err) {
      console.error('Error approving reschedule:', err);
      const isPermission = err?.code === 'permission-denied' || /permission|insufficient/i.test(err?.message || '');
      showAlert(
        isPermission
          ? 'You don’t have permission to approve this reschedule. Make sure you’re a player in this match, the referee, or an admin.'
          : 'Error approving reschedule: ' + (err?.message || err),
        { variant: 'error' }
      );
    }
  }

  async function handleBecomeReferee(matchId) {
    try {
      const match = matches.find(m => m.id === matchId);
      if (!match) return;

      const isPlayer = currentUser && (
        match.matchType === 'doubles'
          ? ((match.team1Players && match.team1Players.includes(currentUser.uid)) ||
             (match.team2Players && match.team2Players.includes(currentUser.uid)))
          : (match.player1Id === currentUser.uid || match.player2Id === currentUser.uid)
      );

      if (isPlayer) {
        showAlert('Players in this match cannot be the referee. Only someone not playing can become referee.', { variant: 'warning' });
        return;
      }

      if (match.refereeId && match.refereeId !== currentUser.uid) {
        // Request to replace referee
        await updateDoc(doc(db, 'matches', matchId), {
          refereeRequestedBy: currentUser.uid,
          refereeRequestedAt: new Date()
        });
        showToast('Referee replacement requested. Waiting for current referee approval.', { variant: 'info' });
      } else {
        // Direct assignment
        await updateDoc(doc(db, 'matches', matchId), {
          refereeId: currentUser.uid,
          refereeRequestedBy: null
        });
        showToast('You are now the referee for this match.', { variant: 'success' });
      }
      fetchMatches();
    } catch (err) {
      console.error('Error becoming referee:', err);
      const isPermission = err?.code === 'permission-denied' || /permission|insufficient/i.test(err?.message || '');
      showAlert(
        isPermission
          ? 'You don’t have permission to update this match. Try refreshing the page and try again.'
          : 'Error: ' + (err?.message || err),
        { variant: 'error' }
      );
    }
  }

  async function handleApproveRefereeReplacement(matchId) {
    try {
      const match = matches.find(m => m.id === matchId);
      if (!match || match.refereeId !== currentUser.uid) {
        showAlert('You are not the current referee', { variant: 'warning' });
        return;
      }

      await updateDoc(doc(db, 'matches', matchId), {
        refereeId: match.refereeRequestedBy,
        refereeRequestedBy: null
      });
      fetchMatches();
      showToast('Referee replacement approved.', { variant: 'success' });
    } catch (err) {
      console.error('Error approving referee replacement:', err);
      const isPermission = err?.code === 'permission-denied' || /permission|insufficient/i.test(err?.message || '');
      showAlert(
        isPermission
          ? 'You don’t have permission to approve referee replacement. Only the current referee can approve. Try refreshing the page.'
          : 'Error: ' + (err?.message || err),
        { variant: 'error' }
      );
    }
  }

  if (loading) {
    return <DataFetchOverlay message="Loading schedule…" />;
  }

  const filteredMatches = getFilteredMatches();
  const myMatchesByRole = filter === 'my-matches' ? getMyMatchesByRole() : null;
  const showUpcomingByDefault = filter === 'upcoming';

  function renderMatchCard(match) {
    let isMyMatch = false;
    if (match.matchType === 'doubles') {
      isMyMatch = currentUser && (
        (match.team1Players && match.team1Players.includes(currentUser.uid)) ||
        (match.team2Players && match.team2Players.includes(currentUser.uid))
      );
    } else {
      isMyMatch = currentUser && (
        match.player1Id === currentUser.uid ||
        match.player2Id === currentUser.uid
      );
    }
    const isReferee = currentUser && match.refereeId === currentUser.uid;
    const isAdmin = userProfile?.isAdmin || userProfile?.isSuperAdmin;
    const canScore = isReferee || isAdmin;
    const canApproveReschedule = (isMyMatch || isReferee || isAdmin) && match.rescheduleRequested;

    return (
      <div key={match.id} className={`match-card schedule-card-ui ${match.status === 'in-progress' ? 'match-card-live' : ''}`}>
        <div className="schedule-card-top">
          <div className="schedule-time-block">
            <span className="schedule-time-text">
              {(match.scheduledAt ?? match.scheduledDate)
                ? (match.scheduledTime && match.scheduledTime.trim()) || formatTimeInTimezone(match.scheduledAt ?? match.scheduledDate, userTimezone)
                : match.status === 'unschedulable'
                  ? 'TBD'
                  : '—'}
            </span>
            {(match.scheduledAt ?? match.scheduledDate) && (
              <span className="schedule-date-muted">
                {formatDateInTimezone(match.scheduledAt ?? match.scheduledDate, userTimezone)}
              </span>
            )}
            {match.status === 'unschedulable' && (
              <span className="schedule-date-muted">Needs scheduling</span>
            )}
          </div>
          <div className="schedule-card-meta schedule-card-meta-top">
            <span className={`schedule-status-badge status-${match.status}`}>
              {match.status === 'in-progress' ? 'Live' : match.status === 'unschedulable' ? 'Needs scheduling' : match.status}
            </span>
            <span className="schedule-round">Match #{match.matchNumber ?? match.round ?? '—'}</span>
          </div>
        </div>
        <div className="match-players">
          <div className="schedule-player schedule-player-1">
            {match.matchType === 'doubles' && (match.team1Players || []).filter(id => getPlayerPhotoUrl(id)).length > 0 ? (
              <div className="match-player-avatars">
                {(match.team1Players || []).filter(id => getPlayerPhotoUrl(id)).map(id => (
                  <CachedAvatar key={id} url={getPlayerPhotoUrl(id)} name={getPlayerName(id)} className="match-player-avatar" />
                ))}
              </div>
            ) : match.matchType !== 'doubles' && getPlayerPhotoUrl(match.player1Id) ? (
              <CachedAvatar url={getPlayerPhotoUrl(match.player1Id)} name={getPlayerName(match.player1Id)} className="match-player-avatar" />
            ) : null}
            <div className="schedule-player-names">
              {match.matchType === 'doubles' && (match.team1Players || []).length > 0 ? (
                (match.team1Players || []).map((id) => (
                  <div key={id} className="schedule-player-name"><PlayerNameWithApartment playerId={id} /></div>
                ))
              ) : (
                <div className="schedule-player-name"><PlayerNameWithApartment playerId={match.player1Id} /></div>
              )}
            </div>
            {match.scores && match.scores.player1 != null && (
              <span className="score">{match.scores.player1}</span>
            )}
          </div>
          <div className="vs-pill">VS</div>
          <div className="schedule-player schedule-player-2">
            {match.matchType === 'doubles' && (match.team2Players || []).filter(id => getPlayerPhotoUrl(id)).length > 0 ? (
              <div className="match-player-avatars">
                {(match.team2Players || []).filter(id => getPlayerPhotoUrl(id)).map(id => (
                  <CachedAvatar key={id} url={getPlayerPhotoUrl(id)} name={getPlayerName(id)} className="match-player-avatar" />
                ))}
              </div>
            ) : match.matchType !== 'doubles' && getPlayerPhotoUrl(match.player2Id) ? (
              <CachedAvatar url={getPlayerPhotoUrl(match.player2Id)} name={getPlayerName(match.player2Id)} className="match-player-avatar" />
            ) : null}
            <div className="schedule-player-names">
              {match.matchType === 'doubles' && (match.team2Players || []).length > 0 ? (
                (match.team2Players || []).map((id) => (
                  <div key={id} className="schedule-player-name"><PlayerNameWithApartment playerId={id} /></div>
                ))
              ) : (
                <div className="schedule-player-name"><PlayerNameWithApartment playerId={match.player2Id} /></div>
              )}
            </div>
            {match.scores && match.scores.player2 != null && (
              <span className="score">{match.scores.player2}</span>
            )}
          </div>
        </div>
        {match.matchType === 'doubles' && (
          <div className="match-type-badge">👥 Doubles Match</div>
        )}
        <div className="schedule-card-meta-bottom">
          {match.refereeId && (
            <div className="match-referee">
              <strong>Referee:</strong> <PlayerNameWithApartment playerId={match.refereeId} />
            </div>
          )}
          {(match.scheduledAt ?? match.scheduledDate) && (
            <div className="match-date">
              <strong>Date:</strong> {formatDateInTimezone(match.scheduledAt ?? match.scheduledDate, userTimezone)}
            </div>
          )}
          {(match.scheduledAt ?? match.scheduledDate) && (
            <div className="match-time">
              <strong>Time:</strong> {(match.scheduledTime && match.scheduledTime.trim()) || formatTimeInTimezone(match.scheduledAt ?? match.scheduledDate, userTimezone)}
            </div>
          )}
          {(match.venueName || match.courtName) && (
            <div className="match-venue">
              <strong>Venue:</strong> {[match.venueName, match.courtName].filter(Boolean).join(' · ') || '—'}
            </div>
          )}
          {match.tournamentId == null && (
            <span className="schedule-round" style={{ marginLeft: '8px' }}>Casual</span>
          )}
          {currentUser && (
            <div className="schedule-unique-views" title="Total unique views of this match score">
              <span className="schedule-unique-views-number">{match.viewCount ?? 0}</span>
              <span className="schedule-unique-views-label">unique views</span>
            </div>
          )}
        </div>
        {currentUser && (
          <div className="schedule-card-reactions">
            <div className="schedule-reaction-team">
              <span className="schedule-reaction-team-label">
                {match.matchType === 'doubles' ? (match.team1Name || 'Team 1') : getPlayerName(match.player1Id) || 'Player 1'}
              </span>
              <MatchReactionStrip
                matchId={match.id}
                team="player1"
                teamName={match.matchType === 'doubles' ? (match.team1Name || 'Team 1') : (getPlayerName(match.player1Id) || 'Player 1')}
                reactionCounts={match.reactionCounts?.player1 || {}}
                onReactionUpdate={(mid, team, emojiIndex, delta) => {
                  setMatches((prev) => prev.map((m) => m.id !== mid ? m : {
                    ...m,
                    reactionCounts: {
                      ...m.reactionCounts,
                      [team]: {
                        ...(m.reactionCounts?.[team] || {}),
                        [emojiIndex]: Math.max(0, ((m.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta)
                      }
                    }
                  }));
                }}
                onOpenReactionsModal={setReactionsModalMatchId}
              />
            </div>
            <div className="schedule-reaction-team">
              <span className="schedule-reaction-team-label">
                {match.matchType === 'doubles' ? (match.team2Name || 'Team 2') : getPlayerName(match.player2Id) || 'Player 2'}
              </span>
              <MatchReactionStrip
                matchId={match.id}
                team="player2"
                teamName={match.matchType === 'doubles' ? (match.team2Name || 'Team 2') : (getPlayerName(match.player2Id) || 'Player 2')}
                reactionCounts={match.reactionCounts?.player2 || {}}
                onReactionUpdate={(mid, team, emojiIndex, delta) => {
                  setMatches((prev) => prev.map((m) => m.id !== mid ? m : {
                    ...m,
                    reactionCounts: {
                      ...m.reactionCounts,
                      [team]: {
                        ...(m.reactionCounts?.[team] || {}),
                        [emojiIndex]: Math.max(0, ((m.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta)
                      }
                    }
                  }));
                }}
                onOpenReactionsModal={setReactionsModalMatchId}
              />
            </div>
          </div>
        )}
        <div className="match-actions">
          {canScore && (match.status === 'scheduled' || match.status === 'in-progress') && (
            <button
              onClick={() => navigate(`/match/${match.id}`)}
              className="btn-primary"
            >
              {match.status === 'in-progress' ? 'Score' : 'Score Match'}
            </button>
          )}
          {currentUser && (!canScore || match.status === 'completed') && (
            <button
              onClick={() => navigate(`/match/${match.id}`)}
              className="btn-secondary"
            >
              View
            </button>
          )}
          {currentUser && !isMyMatch && !isReferee && match.status === 'scheduled' && (
            <button
              onClick={() => handleBecomeReferee(match.id)}
              className="btn-info"
            >
              Become Referee
            </button>
          )}
          {match.matchType === 'doubles' && (
            <div className="match-type-indicator">
              <small>Doubles Match</small>
            </div>
          )}
          {isReferee && match.refereeRequestedBy &&
            match.refereeRequestedBy !== currentUser.uid &&
            match.status !== 'completed' && (
              <button
                onClick={() => handleApproveRefereeReplacement(match.id)}
                className="btn-success"
              >
                Approve Referee Replacement
              </button>
            )}
          {isMyMatch && match.status === 'scheduled' && !match.rescheduleRequested && (
            <button
              onClick={() => setRescheduleModalMatch(match)}
              className="btn-warning"
            >
              Request Reschedule
            </button>
          )}
          {canApproveReschedule &&
            (isAdmin || isReferee || !(Array.isArray(match.rescheduleApprovedBy) ? match.rescheduleApprovedBy : []).includes(currentUser.uid)) && (
              <button
                onClick={() => handleApproveReschedule(match.id)}
                className="btn-success"
              >
                {isAdmin || isReferee ? 'Approve Reschedule (admin/referee)' : 'Approve Reschedule'}
              </button>
            )}
          {match.rescheduleRequested && (() => {
            const approvedBy = Array.isArray(match.rescheduleApprovedBy) ? match.rescheduleApprovedBy : [];
            const teams = getTeamsForApproval(match);
            const enough = hasEnoughApprovals(approvedBy, teams);
            if (enough) return null;
            const pendingTeams = teams.filter((t) => !t.some((id) => approvedBy.includes(id)));
            const reqDate = match.rescheduleRequestedDate;
            const reqTime = match.rescheduleRequestedTime;
            const reqStr = reqDate && reqTime
              ? ` → ${formatDate(reqDate)} at ${reqTime}`
              : '';
            return (
              <span className="reschedule-pending" style={{ fontSize: '12px', color: '#666' }}>
                Waiting for 1 player from each team to approve ({pendingTeams.length} team{pendingTeams.length !== 1 ? 's' : ''} pending){reqStr}
              </span>
            );
          })()}
          {match.status === 'completed' && (
            <button
              onClick={() => navigate(`/match/${match.id}`)}
              className="btn-secondary"
            >
              View Results
            </button>
          )}
          <button
            onClick={() => setHistoryModalMatchId(match.id)}
            className="btn-secondary"
            title="View update history"
          >
            History
          </button>
        </div>
      </div>
    );
  }

  function renderMatchesList(list, emptyMessage = 'No matches found.') {
    if (!list.length) {
      return (
        <div className="no-matches">
          <p>{emptyMessage}</p>
        </div>
      );
    }
    return <>{list.map(m => renderMatchCard(m))}</>;
  }

  const matchCount = filteredMatches.length;
  const showMainList = filter !== 'my-matches';
  const mainListEmpty = showMainList && matchCount === 0;

  return (
    <div className="match-schedule-container schedule-page">
      <header className="schedule-header-row">
        <div className="schedule-heading">
          <h1 className="page-title">Match Schedule</h1>
          <p className="page-subtitle">
            {showUpcomingByDefault ? 'Upcoming matches in chronological order' : 'Fixtures – scheduled, live and completed'}
            {userProfile?.isGuest && (
              <span className="schedule-guest-note"> • Showing public matches only</span>
            )}
            {userProfile?.approvedGroupIds?.length > 0 && !userProfile?.isGuest && (
              <span className="schedule-groups-note"> • Showing matches for your groups</span>
            )}
          </p>
        </div>
        <div className="schedule-header-actions">
          {adminTournaments.length > 0 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowAvailabilityAdminOverlay(true)}
              title="View and edit player availability for your tournaments"
            >
              Player availability
            </button>
          )}
          <button type="button" onClick={() => navigate('/dashboard')} className="btn-secondary btn-back">
            Back to Dashboard
          </button>
        </div>
      </header>

      <GoogleAd slotKey="BANNER" className="google-ad-banner" />

      {(() => {
        const hasMyScheduledMatches = matches.some((m) => {
          if (m.status !== 'scheduled' || !(m.scheduledAt ?? m.scheduledDate)) return false;
          if (m.matchType === 'doubles') {
            return (m.team1Players || []).includes(currentUser?.uid) || (m.team2Players || []).includes(currentUser?.uid);
          }
          return m.player1Id === currentUser?.uid || m.player2Id === currentUser?.uid;
        });
        const showCalendarSection = hasMyScheduledMatches && currentUser;
        const showAvailabilitySection = currentUser && !userProfile?.isGuest;
        const hasAnySection = calendarSyncing || showCalendarSection || showAvailabilitySection;
        if (!hasAnySection) return null;
        return (
          <div className="schedule-calendar-availability-bar card-ui">
            {calendarSyncing && (
              <span className="schedule-calendar-syncing-text">Syncing matches to your Google Calendar…</span>
            )}
            <div className="schedule-calendar-availability-actions">
              {!calendarSyncing && showCalendarSection && (
                <>
                  {!calendarConnected ? (
                    <button
                      type="button"
                      className="btn-primary schedule-calendar-availability-btn"
                      onClick={handleConnectAndSync}
                      disabled={addingToCalendarMatchId === 'connect'}
                    >
                      {addingToCalendarMatchId === 'connect' ? 'Connecting…' : 'Connect Calendar'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary schedule-calendar-availability-btn"
                      onClick={handleResyncCalendar}
                      disabled={addingToCalendarMatchId === 'resync'}
                    >
                      {addingToCalendarMatchId === 'resync' ? 'Syncing…' : 'Resync calendar'}
                    </button>
                  )}
                </>
              )}
              {showAvailabilitySection && (
                <button
                  type="button"
                  className="btn-secondary schedule-calendar-availability-btn"
                  onClick={() => navigate('/dashboard', { state: { openAvailability: true } })}
                >
                  My availability
                </button>
              )}
              <button
                type="button"
                className="schedule-calendar-availability-toggle"
                onClick={() => setCalendarAvailabilityExpanded((v) => !v)}
                aria-expanded={calendarAvailabilityExpanded}
                title={calendarAvailabilityExpanded ? 'Hide details' : 'Show details'}
              >
                {calendarAvailabilityExpanded ? '− Details' : '+ Details'}
              </button>
            </div>
            {calendarAvailabilityExpanded && (
              <div className="schedule-calendar-availability-details">
                {!calendarConnected && showCalendarSection && (
                  <p>Connect your Google Calendar to automatically sync all your matches. Future matches will be added and rescheduled matches will be updated.</p>
                )}
                {calendarConnected && showCalendarSection && (
                  <p>Calendar connected. Matches sync automatically. Only app-created events are updated.</p>
                )}
                {showAvailabilitySection && (
                  <p>Set when you can play so tournament admins can schedule matches at times that work for you.</p>
                )}
              </div>
            )}
          </div>
        );
      })()}

      <div className="schedule-filters-bar card-ui">
        <span className="schedule-filters-label">Filter</span>
        {/* Mobile: native select for easy tap and accessibility */}
        <select
          aria-label="Filter matches"
          className="schedule-filter-select"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="upcoming">Upcoming</option>
          <option value="all">All Matches</option>
          <option value="my-matches">My Matches</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
        </select>
        {/* Desktop: pill buttons */}
        <div className="filters filters-buttons">
          <button
            type="button"
            className={filter === 'upcoming' ? 'filter-active' : 'filter-btn'}
            onClick={() => setFilter('upcoming')}
          >
            Upcoming
          </button>
          <button
            type="button"
            className={filter === 'all' ? 'filter-active' : 'filter-btn'}
            onClick={() => setFilter('all')}
          >
            All Matches
          </button>
          <button
            type="button"
            className={filter === 'my-matches' ? 'filter-active' : 'filter-btn'}
            onClick={() => setFilter('my-matches')}
          >
            My Matches
          </button>
          <button
            type="button"
            className={filter === 'scheduled' ? 'filter-active' : 'filter-btn'}
            onClick={() => setFilter('scheduled')}
          >
            Scheduled
          </button>
          <button
            type="button"
            className={filter === 'completed' ? 'filter-active' : 'filter-btn'}
            onClick={() => setFilter('completed')}
          >
            Completed
          </button>
        </div>
        {showMainList && (
          <span className="schedule-match-count" aria-live="polite">
            {matchCount === 0 ? 'No matches' : `${matchCount} match${matchCount !== 1 ? 'es' : ''}`}
          </span>
        )}
      </div>

      {mainListEmpty && (
        <div className="schedule-empty-state card-ui">
          <div className="schedule-empty-icon">📅</div>
          <h2 className="schedule-empty-title">
            {filter === 'upcoming' ? 'No upcoming matches' : 'No matches found'}
          </h2>
          <p className="schedule-empty-desc">
            {filter === 'upcoming'
              ? 'There are no scheduled or in-progress matches. Try "Completed" to see past matches.'
              : 'There are no matches for the selected filter. Try "Upcoming" or another filter.'}
          </p>
        </div>
      )}

      {filter === 'my-matches' && myMatchesByRole ? (
        <div className="my-matches-sections">
          <section className="my-matches-section">
            <h2 className="my-matches-section-title">
              <span className="my-matches-section-icon">👩‍⚖️</span>
              As referee
            </h2>
            <div className="matches-list">
              {renderMatchesList(myMatchesByRole.referee, 'No matches as referee.')}
            </div>
          </section>
          <section className="my-matches-section">
            <h2 className="my-matches-section-title">
              <span className="my-matches-section-icon">🎾</span>
              Singles matches
            </h2>
            <div className="matches-list">
              {renderMatchesList(myMatchesByRole.singles, 'No singles matches.')}
            </div>
          </section>
          <section className="my-matches-section">
            <h2 className="my-matches-section-title">
              <span className="my-matches-section-icon">👥</span>
              Doubles matches
            </h2>
            <div className="matches-list">
              {renderMatchesList(myMatchesByRole.doubles, 'No doubles matches.')}
            </div>
          </section>
        </div>
      ) : !mainListEmpty ? (
        <div className={`matches-list ${showUpcomingByDefault ? 'matches-list-upcoming' : ''}`}>
          {renderMatchesList(filteredMatches)}
        </div>
      ) : null}

      {historyModalMatchId && (
        <MatchUpdateHistoryModal
          matchId={historyModalMatchId}
          onClose={() => setHistoryModalMatchId(null)}
        />
      )}
      {rescheduleModalMatch && (
        <RescheduleRequestModal
          match={rescheduleModalMatch}
          onConfirm={(date, time) => handleRequestReschedule(rescheduleModalMatch.id, date, time)}
          onCancel={() => setRescheduleModalMatch(null)}
        />
      )}
      {reactionsModalMatchId && (() => {
        const m = matches.find((match) => match.id === reactionsModalMatchId);
        return m ? (
          <MatchReactionsModal
            matchId={reactionsModalMatchId}
            player1Name={m.matchType === 'doubles' ? (m.team1Name || 'Team 1') : (getPlayerName(m.player1Id) || 'Player 1')}
            player2Name={m.matchType === 'doubles' ? (m.team2Name || 'Team 2') : (getPlayerName(m.player2Id) || 'Player 2')}
            onClose={() => setReactionsModalMatchId(null)}
          />
        ) : null;
      })()}
      <PlayerAvailabilityAdminOverlay
        isOpen={showAvailabilityAdminOverlay}
        onClose={() => setShowAvailabilityAdminOverlay(false)}
        tournaments={adminTournaments}
        matches={matches}
        getPlayerName={getPlayerName}
      />
    </div>
  );
}

